import { Check, ChevronDown, Globe2, Search } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { filterCountryOptions } from './phoneNumber';

export default function CountryCombobox({
	options,
	value,
	onChange,
	disabled = false,
	invalid = false,
	loading = false,
	error = false,
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState('');
	const [activeIndex, setActiveIndex] = useState(0);
	const [panelViewportStyle, setPanelViewportStyle] = useState({});
	const rootRef = useRef(null);
	const triggerRef = useRef(null);
	const searchRef = useRef(null);
	const optionRefs = useRef(new Map());
	const listboxId = useId();
	const selected = options.find((option) => option.alpha2 === value) || null;
	const filteredOptions = useMemo(() => filterCountryOptions(options, query), [options, query]);

	useEffect(() => {
		if (!open) return undefined;
		const closeOnOutsidePointer = (event) => {
			if (!rootRef.current?.contains(event.target)) {
				setOpen(false);
				setQuery('');
			}
		};
		document.addEventListener('mousedown', closeOnOutsidePointer);
		return () => document.removeEventListener('mousedown', closeOnOutsidePointer);
	}, [open]);

	useEffect(() => {
		if (open) searchRef.current?.focus();
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;

		const updatePanelViewport = () => {
			const viewport = window.visualViewport;
			const visualHeight = viewport?.height || window.innerHeight;
			const visualBottom = (viewport?.offsetTop || 0) + visualHeight;
			const keyboardInset = Math.max(0, window.innerHeight - visualBottom);
			const edgeMargin = 16;

			setPanelViewportStyle({
				'--country-panel-bottom': `${Math.round(keyboardInset + edgeMargin)}px`,
				'--country-panel-max-height': `${Math.max(0, Math.round(visualHeight - edgeMargin * 2))}px`,
			});
		};

		updatePanelViewport();
		window.addEventListener('resize', updatePanelViewport);
		window.visualViewport?.addEventListener('resize', updatePanelViewport);
		window.visualViewport?.addEventListener('scroll', updatePanelViewport);

		return () => {
			window.removeEventListener('resize', updatePanelViewport);
			window.visualViewport?.removeEventListener('resize', updatePanelViewport);
			window.visualViewport?.removeEventListener('scroll', updatePanelViewport);
		};
	}, [open]);

	const optionId = (option) => `${listboxId}-option-${option.alpha2}`;
	const activeOption = open ? filteredOptions[activeIndex] : null;

	useEffect(() => {
		if (!activeOption) return;
		optionRefs.current.get(activeOption.alpha2)?.scrollIntoView({ block: 'nearest' });
	}, [activeOption]);

	const openList = () => {
		const selectedIndex = filteredOptions.findIndex((option) => option.alpha2 === value);
		setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
		setOpen(true);
	};

	const closeAndFocusTrigger = () => {
		setOpen(false);
		setQuery('');
		triggerRef.current?.focus();
	};

	const choose = (option) => {
		onChange(option);
		closeAndFocusTrigger();
	};

	const handleTriggerKeyDown = (event) => {
		if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
		event.preventDefault();
		if (!open) openList();
	};

	const handleSearchKeyDown = (event) => {
		if (event.key === 'Tab') {
			setOpen(false);
			setQuery('');
			return;
		}
		if (event.key === 'Escape') {
			event.preventDefault();
			closeAndFocusTrigger();
			return;
		}
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActiveIndex((current) => Math.min(current + 1, filteredOptions.length - 1));
			return;
		}
		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActiveIndex((current) => Math.max(current - 1, 0));
			return;
		}
		if (event.key === 'Enter' && activeOption) {
			event.preventDefault();
			choose(activeOption);
		}
	};

	return (
		<div ref={rootRef} className="relative min-w-0">
			<button
				ref={triggerRef}
				type="button"
				role="combobox"
				aria-label="Country"
				aria-expanded={open}
				aria-controls={listboxId}
				aria-haspopup="listbox"
				aria-activedescendant={activeOption ? optionId(activeOption) : undefined}
				disabled={disabled}
				onClick={() => {
					if (open) closeAndFocusTrigger();
					else openList();
				}}
				onKeyDown={handleTriggerKeyDown}
				className={`input-field flex h-12 min-w-0 items-center gap-2 px-3 text-left ${invalid ? 'border-red-400' : ''}`}
			>
				{selected ? (
					<>
						<span aria-hidden="true" className="text-lg leading-none">{selected.flag}</span>
						<span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{selected.alpha2}</span>
						<span className="shrink-0 text-sm text-slate-600">+{selected.callingCode}</span>
					</>
				) : (
					<>
						<Globe2 aria-hidden="true" className="h-4 w-4 shrink-0 text-slate-400" />
						<span className="min-w-0 flex-1 truncate text-sm text-slate-500">Country</span>
					</>
				)}
				<ChevronDown aria-hidden="true" className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
			</button>

			{open ? (
				<div
					data-country-panel
					style={panelViewportStyle}
					className="country-combobox-panel z-30 overflow-hidden rounded border border-slate-200 bg-white shadow-lg"
				>
					<div className="border-b border-slate-200 p-2">
						<label className="relative block">
							<span className="sr-only">Search countries</span>
							<Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
							<input
								ref={searchRef}
								type="search"
								aria-label="Search countries"
								aria-controls={listboxId}
								aria-activedescendant={activeOption ? optionId(activeOption) : undefined}
								value={query}
								onChange={(event) => {
									setQuery(event.target.value);
									setActiveIndex(0);
								}}
								onKeyDown={handleSearchKeyDown}
								placeholder="Country, code, or +dial code"
								className="input-field h-10 pl-9 text-base sm:text-sm"
							/>
						</label>
					</div>
					<div id={listboxId} role="listbox" aria-label="Countries" className="min-h-0 max-h-64 flex-1 overflow-y-auto p-1">
						{loading ? <p className="px-3 py-4 text-center text-sm text-slate-500">Loading countries...</p> : null}
						{!loading && error ? <p className="px-3 py-4 text-center text-sm text-red-700">Countries unavailable</p> : null}
						{!loading && !error && filteredOptions.length === 0 ? <p className="px-3 py-4 text-center text-sm text-slate-500">No countries found</p> : null}
						{!loading && !error ? filteredOptions.map((option) => (
							<button
								key={option.alpha2}
								ref={(node) => {
									if (node) optionRefs.current.set(option.alpha2, node);
									else optionRefs.current.delete(option.alpha2);
								}}
								id={optionId(option)}
								type="button"
								role="option"
								tabIndex={-1}
								aria-selected={option.alpha2 === value}
								onClick={() => choose(option)}
								onMouseEnter={() => setActiveIndex(filteredOptions.indexOf(option))}
								className={`flex min-h-11 w-full items-center gap-3 rounded px-3 py-2 text-left ${activeOption?.alpha2 === option.alpha2 ? 'bg-cyan-50' : 'hover:bg-slate-50'}`}
							>
								<span data-country-details className="flex min-w-0 flex-1 items-center gap-3">
									<span aria-hidden="true" className="w-6 shrink-0 text-lg leading-none">{option.flag}</span>
									<span className="min-w-0 truncate text-sm font-medium text-slate-800">{option.name} <span className="font-normal text-slate-500">({option.alpha2})</span></span>
								</span>
								<span data-calling-code className="shrink-0 text-sm font-semibold text-slate-600">+{option.callingCode}</span>
								{option.alpha2 === value ? <Check aria-hidden="true" className="h-4 w-4 shrink-0 text-cyan-800" /> : null}
							</button>
						)) : null}
					</div>
				</div>
			) : null}
		</div>
	);
}
